import { calculateHeaderGroupHeight } from '@theme/critical';
import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor } from '@theme/utilities';

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  #menuDrawerHiddenWidth = null;
  #intersectionObserver = null;
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;
    const { height } = entry.target.getBoundingClientRect();
    document.body.style.setProperty('--header-height', `${height}px`);

    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.refs.headerDrawerContainer.classList.remove('desktop:hidden');
      this.#menuDrawerHiddenWidth = window.innerWidth;
      this.refs.headerMenu.classList.add('hidden');
    } else {
      this.refs.headerDrawerContainer.classList.add('desktop:hidden');
      this.#menuDrawerHiddenWidth = null;
      this.refs.headerMenu.classList.remove('hidden');
    }
  }

  #observeStickyPosition(stickyMode = 'always') {
    if (this.#intersectionObserver) return;

    const config = { threshold: stickyMode === 'always' ? 1 : 0 };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;
      const { isIntersecting } = entry;

      // Handle different sticky modes
      if (stickyMode === 'always') {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        changeMetaThemeColor(this.refs.headerRowTop);
      } else if (stickyMode === 'scroll-up') {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        this.#handleScrollUpMode();
      } else if (stickyMode === 'hide-show-scroll') {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        this.#handleHideShowScrollMode();
      }
    }, config);

    this.#intersectionObserver.observe(this);
  }

  #handleScrollUpMode() {
    let lastScrollY = window.scrollY;
    let ticking = false;

    const updateScrollDirection = () => {
      const scrollY = window.scrollY;
      const direction = scrollY > lastScrollY ? 'down' : 'up';
      
      if (direction === 'up' && scrollY > 100) {
        this.dataset.stickyState = 'active';
      } else if (direction === 'down') {
        this.dataset.stickyState = 'inactive';
      }
      
      lastScrollY = scrollY;
      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(updateScrollDirection);
        ticking = true;
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
  }

  #handleHideShowScrollMode() {
    let lastScrollY = window.scrollY;
    let ticking = false;

    const updateHideShowScroll = () => {
      const scrollY = window.scrollY;
      
      if (scrollY > 100) {
        this.dataset.stickyState = 'active';
      } else {
        this.dataset.stickyState = 'idle';
      }
      
      lastScrollY = scrollY;
      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(updateHideShowScroll);
        ticking = true;
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
  }

  #handleResize() {
    let resizeTimeout = null;
    let currentMode = window.innerWidth >= 750 ? 'desktop' : 'mobile';
    
    const handleResizeEvent = () => {
      if (resizeTimeout) clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const isDesktop = window.innerWidth >= 750;
        const newMode = isDesktop ? 'desktop' : 'mobile';
        
        // Only switch if we actually changed device type
        if (newMode !== currentMode) {
          currentMode = newMode;
          const newStickyMode = isDesktop ? this.desktopStickyMode : this.mobileStickyMode;
          
          // Disconnect current observer
          if (this.#intersectionObserver) {
            this.#intersectionObserver.disconnect();
            this.#intersectionObserver = null;
          }
          
          // Clear any existing sticky state and classes
          this.removeAttribute('data-sticky-state');
          this.classList.remove('header--scrolled', 'header--transparent', 'header--white-text');
          
          // Update attributes for current device
          if (isDesktop) {
            this.setAttribute('sticky', this.desktopStickyMode || 'never');
            if (this.mobileStickyMode) {
              this.setAttribute('data-mobile-sticky', this.mobileStickyMode);
            }
          } else {
            // On mobile, ensure desktop sticky is set to never to prevent CSS conflicts
            this.setAttribute('sticky', 'never');
            this.setAttribute('data-mobile-sticky', this.mobileStickyMode || 'never');
          }
          
          // Re-initialize with new sticky mode if needed
          if (newStickyMode && newStickyMode !== 'never') {
            this.#observeStickyPosition(newStickyMode);
          }
          
          // Re-apply header state for new device type
          this.#setInitialHeaderState(isDesktop);
          
          console.log(`Device switched to: ${newMode} - Sticky mode: ${newStickyMode}`);
        }
      }, 50); // Faster response for DevTools
    };

    // Listen to multiple events for better DevTools detection
    window.addEventListener('resize', handleResizeEvent, { passive: true });
    
    // Additional listener for DevTools mobile view changes
    const mediaQuery = window.matchMedia('(max-width: 749px)');
    const handleMediaChange = (e) => {
      // Force a resize event when media query changes (DevTools mobile view)
      setTimeout(handleResizeEvent, 10);
    };
    
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleMediaChange);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handleMediaChange);
    }
  }

  #setInitialHeaderState(isDesktop) {
    const isHomepage = window.location.pathname === '/';
    
    // Force remove all header state classes first
    this.classList.remove('header--transparent', 'header--scrolled', 'header--white-text');
    
    // Only apply transparent header on desktop homepage
    // Mobile NEVER gets transparent state to prevent blinking
    if (isHomepage && isDesktop) {
      this.classList.add('header--transparent');
      this.classList.add('header--white-text');
    } else {
      this.classList.add('header--scrolled');
    }
    
    // Ensure mobile always has scrolled state
    if (!isDesktop) {
      this.classList.add('header--scrolled');
      this.classList.remove('header--transparent', 'header--white-text');
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', (event) => {
      this.#updateMenuVisibility(event.detail.minimumReached);
    });

    const desktopStickyMode = this.getAttribute('sticky');
    const mobileStickyMode = this.getAttribute('data-mobile-sticky');
    const isTransparent = this.getAttribute('transparent') === 'true';

    // Determine current sticky mode based on screen size
    const isDesktop = window.innerWidth >= 750;
    const currentStickyMode = isDesktop ? desktopStickyMode : mobileStickyMode;

    // Store both modes for resize handling
    this.desktopStickyMode = desktopStickyMode;
    this.mobileStickyMode = mobileStickyMode;

    // Ensure correct attributes are set for current device
    if (!isDesktop) {
      // On mobile, force desktop sticky to never to prevent CSS conflicts
      this.setAttribute('sticky', 'never');
      this.setAttribute('data-mobile-sticky', mobileStickyMode || 'never');
    }

    // Handle device-specific sticky header functionality
    if (currentStickyMode && currentStickyMode !== 'never') {
      this.#observeStickyPosition(currentStickyMode);
    }

    // Apply initial header state based on device and page
    this.#setInitialHeaderState(isDesktop);

    // Handle resize events to switch between desktop/mobile sticky modes
    this.#handleResize();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

// Immediately apply default transparent/white header before DOMContentLoaded
(() => {
  const header = document.querySelector('#header-component');
  if (!header) return;

  const isHomepage = window.location.pathname === '/';
  const isDesktop = () => window.innerWidth >= 750; // Desktop breakpoint
  const stickyMode = header.getAttribute('sticky');

  // Apply initial background and menu colors - ONLY on desktop homepage
  if (isHomepage && isDesktop()) {
    header.classList.add('header--transparent');
    header.classList.add('header--white-text');
    header.classList.remove('header--scrolled');
  } else {
    header.classList.remove('header--transparent');
    header.classList.remove('header--white-text');
    header.classList.add('header--scrolled');
  }

  const updateHeaderBackground = () => {
    const scrollTop = window.scrollY;

    // Only apply transparent header behavior on desktop + homepage
    if (isHomepage && isDesktop()) {
      // Calculate 10% of total page height
      const pageHeight = document.documentElement.scrollHeight;
      const triggerPoint = pageHeight * 0.10; // 10% of page height
      
      console.log(`Page height: ${pageHeight}px, Trigger point (10%): ${triggerPoint}px, Current scroll: ${scrollTop}px`);
      
      if (scrollTop > triggerPoint) {
        header.classList.add('header--scrolled');
        header.classList.remove('header--transparent');
        header.classList.remove('header--white-text');
      } else {
        header.classList.remove('header--scrolled');
        header.classList.add('header--transparent');
        header.classList.add('header--white-text');
      }
    } else {
      // Mobile or non-homepage: always white background with black text
      header.classList.add('header--scrolled');
      header.classList.remove('header--transparent');
      header.classList.remove('header--white-text');
    }

    // Handle sticky header visibility based on mode
    if (stickyMode === 'hide-show-scroll' && header instanceof HTMLElement) {
      if (scrollTop > 100) {
        header.dataset.stickyState = 'active';
      } else {
        header.dataset.stickyState = 'idle';
      }
    }
  };

  window.addEventListener('scroll', updateHeaderBackground);
  
  // Re-check on resize (in case user switches from desktop to mobile view)
  window.addEventListener('resize', updateHeaderBackground);
  
  updateHeaderBackground();

  // Header group resizing
  const headerGroup = document.querySelector('#header-group');
  if (headerGroup) {
    const resizeObserver = new ResizeObserver(() => calculateHeaderGroupHeight(header, headerGroup));

    Array.from(headerGroup.children).forEach((child) => {
      if (child !== header && child instanceof HTMLElement) resizeObserver.observe(child);
    });

    const mutationObserver = new MutationObserver(() => {
      Array.from(headerGroup.children).forEach((child) => {
        if (child !== header && child instanceof HTMLElement) resizeObserver.observe(child);
      });
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
})();